import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useIsRedoAdmin } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import type { SVGProps } from "react";

// Temporary concept icon for Concierge: a human silhouette with star accents to
// suggest "AI acting like a person". Replace this with a real design-system
// SVG once we have a polished permanent icon.
function ConciergeConceptIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" viewBox="0 0 24 24" {...props}>
      <circle
        cx="12"
        cy="8.25"
        r="3.5"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path
        d="M6.4 18.7C7.2 15.8 9.25 14.3 12 14.3C14.75 14.3 16.8 15.8 17.6 18.7"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
      <path
        d="M18.95 2.7L20.1 -0.3L21.25 2.7L24.25 3.85L21.25 5L20.1 8L18.95 5L15.95 3.85L18.95 2.7Z"
        fill="currentColor"
      />
      <path
        d="M1.55 9.15L2.5 6.65L3.45 9.15L5.95 10.1L3.45 11.05L2.5 13.55L1.55 11.05L-0.95 10.1L1.55 9.15Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function useShopperAgentNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const isRedoAdmin = useIsRedoAdmin();

  const flagLoad = useLoad(
    (signal) =>
      marketingRpcClient.getBooleanFeatureFlag(
        { key: FeatureFlagKey.SHOPPER_AGENT },
        { signal },
      ),
    [marketingRpcClient],
  );

  const isEnabled = flagLoad.value?.enabled ?? false;
  if (!isEnabled || !isRedoAdmin) return null;

  const primaryTab: LinkPrimaryTab = {
    id: "shopper-agent",
    hasNotification: false,
    UnfilledIcon: ConciergeConceptIcon,
    FilledIcon: ConciergeConceptIcon,
    type: "link",
    pathRoot: routes.shopperAgentHref,
    href: routes.shopperAgentHref,
    friendlyName: "Concierge",
  };

  return { primaryTab, secondaryNavigationData: undefined };
}
